import { useEffect, useState } from 'react';

const MAX_COMPLETED_IDS = 500;
const completedTypewriterIds = new Set<string>();
const completedTypewriterOrder: string[] = [];

const toMessageKey = (id: string | number | null | undefined): string | null => {
  if (id === null || id === undefined) return null;
  const key = String(id);
  if (key.startsWith('temp_')) return null;
  return key;
};

const markCompleted = (key: string) => {
  if (completedTypewriterIds.has(key)) return;
  completedTypewriterIds.add(key);
  completedTypewriterOrder.push(key);
  while (completedTypewriterOrder.length > MAX_COMPLETED_IDS) {
    const oldest = completedTypewriterOrder.shift();
    if (oldest) completedTypewriterIds.delete(oldest);
  }
};

export type UseTypewriterOptions = {
  /** When false, shows the full text immediately. */
  enabled: boolean;
  /** Milliseconds between characters. */
  speedMs?: number;
  /** Stable id so the same message is not re-typed after completion. */
  messageKey?: string | number | null;
  /** Called as characters appear (e.g. to keep chat scrolled). */
  onProgress?: () => void;
};

/**
 * Reveals `fullText` one character at a time when `enabled`.
 * Already-completed message keys skip animation.
 */
export const useTypewriter = (
  fullText: string,
  { enabled, speedMs = 16, messageKey, onProgress }: UseTypewriterOptions,
) => {
  const key = toMessageKey(messageKey);
  const alreadyDone = key !== null && completedTypewriterIds.has(key);
  const shouldAnimate = enabled && !!fullText && !alreadyDone;

  const [displayedText, setDisplayedText] = useState(() =>
    shouldAnimate ? '' : fullText || '',
  );
  const [isComplete, setIsComplete] = useState(() => !shouldAnimate);
  const [skippedAnimation, setSkippedAnimation] = useState(() => !shouldAnimate);

  useEffect(() => {
    const text = fullText || '';
    const keyNow = toMessageKey(messageKey);
    const skip = !enabled || !text || (keyNow !== null && completedTypewriterIds.has(keyNow));

    if (skip) {
      setDisplayedText(text);
      setIsComplete(true);
      setSkippedAnimation(true);
      return;
    }

    setSkippedAnimation(false);
    setDisplayedText('');
    setIsComplete(false);
    let index = 0;

    // Slightly faster for long replies so wait time stays reasonable.
    const intervalMs = text.length > 280 ? Math.max(8, speedMs - 6) : speedMs;

    const timer = window.setInterval(() => {
      index += 1;
      setDisplayedText(text.slice(0, index));
      onProgress?.();

      if (index >= text.length) {
        window.clearInterval(timer);
        setIsComplete(true);
        if (keyNow !== null) {
          markCompleted(keyNow);
        }
      }
    }, intervalMs);

    return () => window.clearInterval(timer);
  }, [fullText, enabled, speedMs, messageKey, onProgress]);

  return { displayedText, isComplete, skippedAnimation };
};
